import type { ReactNode } from 'react';
import {
  Pill,
  SurfacePagePanel,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { cn } from '@/shared/cn';
import type { RoleTable } from '@/versions/v1/schema/plant-role';
import {
  KpiCard,
  Legend,
  MeterBar,
  kpiCardPropsFromTile,
} from '@/versions/v1/modules/_viz';
import type { KpiTile } from '@/versions/v1/schema/plant-role';
import {
  deltaCellClass,
  reagentDeltaClass,
  reagentStandingFindingLabel,
  type ReagentStandingFinding,
  triadColumnClass,
} from './RoleLanding.transform';
import {
  gmMonthlyAgCard,
  gmPbAgConcentrateCard,
  gmZincConcentrateCard,
} from './RoleLandingPanels.transform';

type PanelProps = {
  title: string;
  subtitle?: string;
  footnote?: string;
  className?: string;
  children: ReactNode;
  legend?: boolean;
  meta?: ReactNode;
  maxHeight?: string;
};

export function DashboardPanel({
  title,
  subtitle,
  footnote,
  className,
  children,
  legend = false,
  meta,
  maxHeight,
}: PanelProps) {
  return (
    <SurfacePagePanel
      title={title}
      subtitle={subtitle}
      meta={meta ?? (legend ? <Legend /> : undefined)}
      maxHeight={maxHeight}
      className={className}
    >
      {children}
      {footnote ? <p className="mt-3 text-xs text-muted">{footnote}</p> : null}
    </SurfacePagePanel>
  );
}

export function KpiStrip({
  kpis,
  className,
}: {
  kpis: readonly KpiTile[];
  className?: string;
}) {
  // One column per card (the deck strips run 4–7 across); an inline template
  // rather than a dynamic `col-span-N` class, which Tailwind would not generate.
  return (
    <div
      className={cn('grid gap-4', className)}
      style={{ gridTemplateColumns: `repeat(${Math.max(kpis.length, 1)}, minmax(0, 1fr))` }}
    >
      {kpis.map((kpi) => (
        <KpiCard key={kpi.id} {...kpiCardPropsFromTile(kpi)} />
      ))}
    </div>
  );
}

export function RoleDataTable({
  table,
  triad = false,
  deltaColumns,
  standingFindings,
  onRowClick,
}: {
  table: RoleTable;
  triad?: boolean;
  deltaColumns?: readonly string[];
  standingFindings?: ReadonlyMap<string, ReagentStandingFinding>;
  onRowClick?: (rowId: string) => void;
}) {
  const deltaSet = new Set(deltaColumns ?? ['Δ vs target', 'Δ', 'Δ vs Target']);
  return (
    <Table clickableRows={Boolean(onRowClick)}>
      <THead>
        <TR>
          {table.columns.map((column) => (
            <TH key={column}>{column}</TH>
          ))}
        </TR>
      </THead>
      <TBody>
        {table.rows.map((row) => {
          const finding = standingFindings?.get(row.id);
          return (
            <TR
              key={row.id}
              onRowClick={onRowClick ? () => onRowClick(row.id) : undefined}
              className={finding ? 'bg-[var(--accent-soft)]' : undefined}
            >
              {row.cells.map((cell, index) => {
                const column = table.columns[index] ?? '';
                const triadClass = triad ? triadColumnClass(column) : undefined;
                const deltaClass = deltaSet.has(column) ? deltaCellClass(cell) : undefined;
                const reagentDelta =
                  column === 'Δ' ? reagentDeltaClass(cell) ?? deltaClass : deltaClass;
                return (
                  <TD
                    key={`${row.id}-${index}`}
                    className={cn(
                      index === 0 ? 'font-medium text-zinc-900 dark:text-zinc-100' : 'tabular',
                      triadClass,
                      reagentDelta,
                      finding && column === 'Δ' ? 'text-[var(--state-warning)] font-medium' : undefined,
                    )}
                  >
                    {column === 'Δ' && finding ? (
                      <span className="inline-flex items-center gap-2">
                        {statusCell(cell)}
                        <span className="text-xs font-medium text-[var(--state-warning)]">
                          {reagentStandingFindingLabel(finding)}
                        </span>
                      </span>
                    ) : (
                      statusCell(cell)
                    )}
                  </TD>
                );
              })}
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}

export function GmConcentrateSummary({
  compact = false,
  variant = 'gm',
  showMoney = true,
}: {
  compact?: boolean;
  variant?: 'gm' | 'met';
  showMoney?: boolean;
}) {
  const monthlyAg = variant === 'met' || !compact ? gmMonthlyAgCard(showMoney) : undefined;
  const zincCard = gmZincConcentrateCard();
  const pbAgCard = gmPbAgConcentrateCard();
  return (
    <DashboardPanel title="Concentrate summary" subtitle={compact ? undefined : 'This month'}>
      <div className={compact ? 'space-y-3' : 'space-y-4'}>
        {zincCard ? (
          <div className={compact ? 'space-y-1.5 border-b border-[var(--hairline)] pb-3' : 'space-y-2 border-b border-[var(--hairline)] pb-4'}>
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{zincCard.title}</p>
            <p className="text-xs text-muted">{zincCard.valueLine}</p>
            <MeterBar
              value={zincCard.value}
              max={zincCard.max}
              marker={{ value: zincCard.markerValue, label: zincCard.markerLabel }}
              tone={zincCard.tone}
            />
            <p
              className={cn(
                'text-xs font-medium',
                zincCard.tone === 'ok' ? 'text-[var(--state-online)]' : 'text-[var(--state-blocked)]',
              )}
            >
              {zincCard.note}
            </p>
          </div>
        ) : null}
        {pbAgCard ? (
          <div className={compact ? 'space-y-1.5 border-b border-[var(--hairline)] pb-3' : 'space-y-2 border-b border-[var(--hairline)] pb-4'}>
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{pbAgCard.title}</p>
            <p className="text-xs text-muted">{pbAgCard.valueLine}</p>
            <MeterBar
              value={pbAgCard.agValue}
              max={pbAgCard.max}
              marker={{ value: pbAgCard.markerValue, label: pbAgCard.markerLabel }}
              tone={pbAgCard.tone}
            />
          </div>
        ) : null}
        {monthlyAg ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{monthlyAg.title}</p>
            <p className="text-xs text-muted">{monthlyAg.valueLine}</p>
            <p className="text-xs font-medium text-[var(--state-warning)]">{monthlyAg.note}</p>
          </div>
        ) : null}
      </div>
    </DashboardPanel>
  );
}

function statusCell(value: string) {
  if (value === '✓' || value === '⟳' || value === '○') {
    return value;
  }
  if (
    value === 'OK' ||
    value === 'RUN' ||
    value === 'Done' ||
    value === 'REG' ||
    value === 'CLOSED' ||
    value === 'Auto'
  ) {
    return <Pill tone="success">{value}</Pill>;
  }
  if (
    value === 'WARN' ||
    value === 'HIGH' ||
    value === 'LOW' ||
    value === 'In progress' ||
    value === 'Due' ||
    value === 'LATE' ||
    value === 'PENDING' ||
    value === 'LIMS?' ||
    value === 'DUE' ||
    value === 'WATCH' ||
    value === 'REVIEW' ||
    value === 'REORDER' ||
    value === 'URGENT'
  ) {
    return <Pill tone="warning">{value}</Pill>;
  }
  if (value === 'MISS' || value === 'Manual') {
    return <Pill tone="danger">{value}</Pill>;
  }
  return value;
}
